import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { getFirstAndLastEmiDates, formatToISTDate, generateLoanSummaryForMember,
  resolveEffectiveStartDate,
} from "../../../utils/utils";

import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const CollectionReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getCollectionReportData`,
          { params: { id } }
        );
        // console.log(
        //   "getCollectionReportData: " + JSON.stringify(response.data)
        // );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) =>
        [
          data.permanentDoorNo,
          data.permanentStreetNo,
          data.permanentPanchayat,
          data.permanentTaluk,
          data.permanentDistrict,
        ]
          .filter(Boolean)
          .join(", ") + (data.permanentPincode ? ` - ${data.permanentPincode}` : ""),
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    {
      label: "Sub Category",
      render: (data) => data.businessDetails?.natureOfBusiness || "",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data.proposedLoanDetails?.rateOfInterest || "",
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? formatToISTDate(data.branchManagerStatusUpdatedAt)
          : "",
    },

    {
      label: "EMI Date",
      render: (data) =>
        data.receiptEmiDate ? formatToISTDate(data.receiptEmiDate) : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        if (!data.proposedLoanDetails) return "";
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? formatToISTDate(lastEmiDate) : "";
      },
    },
    {
      label: "Loan Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? data.sanctionedLoanAmountBySanctionCommittee
          : "",
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    {
      label: "Total Principal Paid",
      render: (data) => data.totalPrincipalPaid,
    },
    { label: "Total Interest Paid", render: (data) => data.totalInterestPaid },
    { label: "Total EMI Paid", render: (data) => data.totalEmiPaid },
    { label: "EMI Amount", render: (data) => data.receiptEmiAmount },
    { label: "Received Amount", render: (data) => data.receivedAmount },
    { label: "Description", render: (data) => data.receiptDescription },
    {
      label: "Receipt No",
      render: (data) => data.collectionApproval?.receiptNo || "",
    },
    {
      label: "Collected Date",
      render: (data) =>
        data.collectedDate ? formatToISTDate(data.collectedDate) : "",
    },
    {
      label: "Collected Time",
      render: (data) =>
        data.collectedDate
          ? new Date(data.collectedDate).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "CRO Submitted Date",
      render: (data) =>
        data.collectionApproval?.fieldManagerStatusUpdatedAt
          ? formatToISTDate(data.collectionApproval?.fieldManagerStatusUpdatedAt)
          : "",
    },
    {
      label: "CRO Submitted Time",
      render: (data) =>
        data.collectionApproval?.fieldManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.fieldManagerStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "Branch Manager Approved Date",
      render: (data) =>
        data.collectionApproval?.branchManagerStatusUpdatedAt
          ? formatToISTDate(data.collectionApproval?.branchManagerStatusUpdatedAt)
          : "",
    },
    {
      label: "Branch Manager Approved Time",
      render: (data) =>
        data.collectionApproval?.branchManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.branchManagerStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "MIS Approved Date",
      render: (data) =>
        data.collectionApproval?.misStatusUpdatedAt
          ? formatToISTDate(data.collectionApproval?.misStatusUpdatedAt)
          : "",
    },
    {
      label: "MIS Approved Time",
      render: (data) =>
        data.collectionApproval?.misStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.misStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "Accounts Manager Approved Date",
      render: (data) =>
        data.collectionApproval?.accountManagerStatusUpdatedAt
          ? formatToISTDate(data.collectionApproval?.accountManagerStatusUpdatedAt)
          : "",
    },
    {
      label: "Accounts Manager Approved Time",
      render: (data) =>
        data.collectionApproval?.accountManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.accountManagerStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
  ];

  const branchOptions = [...new Set(reportData.map((data) => data?.branchName))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [...new Set(reportData.map((data) => data?.username))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const filterData = (data) => {
    let filteredData = data;

    // Check if both dates are selected
    if (fromDate && toDate) {
      filteredData = filteredData.filter((item) => {
        const emiDate = new Date(item.receiptEmiDate);
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        return emiDate >= start && emiDate <= end;
      });

    } else {
      filteredData = []; // Return an empty array if no date is selected.
    }

    // Filter by selected branch
    if (selectedBranchFilter) {
      filteredData = filteredData.filter(
        (item) => item?.branchName === selectedBranchFilter.value
      );
    }

    // Filter by selected employee
    if (selectedEmployeeFilter) {
      filteredData = filteredData.filter(
        (item) => item?.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filteredData = filteredData.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    // Calculate loan summary using the unified utility function
    filteredData = filteredData.map((item) => {
      if (!item.proposedLoanDetails) return item;
      
      const loanSummary = generateLoanSummaryForMember(
        item,
        "2000-01-01",
        toDate || new Date().toISOString().split("T")[0]
      );

      return {
        ...item,
        ...loanSummary,
      };
    });

    return filteredData;
  };

  const totalPages = Math.ceil(filterData(reportData).length / itemsPerPage);

  const filteredData = filterData(reportData).slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Collection Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={filteredData}
                reportName="CollectionReport"
                excelData={filterData(reportData)}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollectionReport;